// File Import adapter — loads rides from a user-supplied JSON file.
// Two modes:
//   - oneShot: drop / browse → read once, no state retained.
//   - sync: pick via showOpenFilePicker, persist FileSystemFileHandle in meta,
//     re-read on app load and via Sync now. Enables Dropbox / iCloud / OneDrive
//     to act as a sync source by writing to a tracked local file.
//
// `kind` is always 'sync' so settings.js wires file-import as the active sync
// adapter; `sync()` returns an empty no-op when no handle is configured.

import { getMeta, setMeta } from '../db.js';
import { normaliseRides } from './normaliseRides.js';
import { reportError } from '../telemetry.js';

const HANDLE_META_KEY = 'fileImport_handle';

const fileImportAdapter = {
    id: 'file-import',
    name: 'File Upload',
    kind: 'sync',
    description: 'Load rides from a previously exported JSON file',
    infoText: 'Import rides from a previously exported JSON file. Only new rides are added — existing rides are kept.',

    async renderConfig(container, onComplete, onDisconnect) {
        this._onDisconnect = onDisconnect;
        const handle = await getMeta(HANDLE_META_KEY);
        const lastOp = await getMeta('lastOp_file-import');
        const statusText = lastOp
            ? `${lastOp.message} · ${new Date(lastOp.timestamp).toLocaleString()}`
            : '';

        if (handle) {
            await this._renderSyncedView(container, handle, statusText, onComplete);
            return;
        }

        this._renderPickerView(container, statusText, onComplete);
    },

    async _renderSyncedView(container, handle, statusText, onComplete) {
        // A connected (file-sync) source that isn't active reads "Activate" —
        // re-reading the file also makes it the active source (ADR-0005, D1).
        const isActiveSource = (await getMeta('activeAdapter')) === 'file-import';
        container.innerHTML = `
            <div class="url-display"></div>
            <button id="btn-file-sync" class="btn-brut btn-brut--primary adapter-action-sync" type="button">
                ${isActiveSource ? 'Sync now' : 'Activate'}
            </button>
            <button id="btn-file-disconnect" class="btn-brut" type="button">
                Disconnect
            </button>
            <div id="import-result" class="hint"></div>
            <div class="adapter-status" aria-live="polite">${statusText}</div>
        `;

        const urlDisplay = container.querySelector('.url-display');
        urlDisplay.textContent = handle.name;
        urlDisplay.title = handle.name;

        const resultEl = container.querySelector('#import-result');

        container.querySelector('#btn-file-sync').addEventListener('click', async () => {
            resultEl.className = 'hint';
            resultEl.textContent = 'Reading file...';
            try {
                const result = await this.sync(msg => { resultEl.textContent = msg; });
                if (!result.message) {
                    resultEl.className = 'hint hint--error';
                    resultEl.textContent = 'Could not read file. Disconnect and pick the file again to re-grant access.';
                    return;
                }
                resultEl.className = 'hint hint--success';
                resultEl.textContent = result.message;
                onComplete(result);
            } catch (e) {
                reportError(e, { adapter: 'file-import', op: 'sync-now' });
                resultEl.className = 'hint hint--error';
                resultEl.textContent = `Error: ${e.message}`;
            }
        });

        container.querySelector('#btn-file-disconnect')
            .addEventListener('click', () => this._onDisconnect?.());
    },

    _renderPickerView(container, statusText, onComplete) {
        const supportsFsAccess = typeof window !== 'undefined' && 'showOpenFilePicker' in window;

        const syncToggleHtml = supportsFsAccess ? `
            <label class="sync-toggle">
                <input type="checkbox" id="file-sync-toggle">
                <span class="sync-toggle__body">
                    <span class="sync-toggle__label">Keep in sync with this file</span>
                    <span class="sync-toggle__hint">Re-reads the file each time the app opens. Useful with Dropbox, iCloud Drive, OneDrive…</span>
                </span>
            </label>
        ` : '';

        container.innerHTML = `
            <label class="drop-zone" id="file-drop-zone" for="file-input">
                Drop a .json file here<br>
                or <span class="drop-zone__browse">browse</span>
                <input type="file" id="file-input" accept=".json" hidden>
            </label>
            ${syncToggleHtml}
            <div id="import-result" class="hint"></div>
            <div class="adapter-status" aria-live="polite">${statusText}</div>
        `;

        const dropZone = container.querySelector('#file-drop-zone');
        const fileInput = container.querySelector('#file-input');
        const syncToggle = container.querySelector('#file-sync-toggle');
        const resultEl = container.querySelector('#import-result');

        // Browse / click on drop zone — branches based on sync toggle.
        dropZone.addEventListener('click', async (e) => {
            if (!syncToggle?.checked) return;
            e.preventDefault();
            e.stopPropagation();
            try {
                const [pickedHandle] = await window.showOpenFilePicker({
                    types: [{ description: 'JSON file', accept: { 'application/json': ['.json'] } }],
                    multiple: false,
                });
                await this._processHandle(pickedHandle, resultEl, container, onComplete);
            } catch (err) {
                if (err.name === 'AbortError') return;
                reportError(err, { adapter: 'file-import', op: 'pick-handle' });
                resultEl.className = 'hint hint--error';
                resultEl.textContent = `Error: ${err.message}`;
            }
        });

        fileInput.addEventListener('change', () => {
            if (fileInput.files[0]) this._processFile(fileInput.files[0], resultEl, onComplete);
        });

        dropZone.addEventListener('dragover', e => {
            e.preventDefault();
            dropZone.classList.add('drag-over');
        });
        dropZone.addEventListener('dragleave', () => dropZone.classList.remove('drag-over'));
        dropZone.addEventListener('drop', e => {
            e.preventDefault();
            dropZone.classList.remove('drag-over');
            if (syncToggle?.checked) {
                resultEl.className = 'hint hint--error';
                resultEl.textContent = 'Click "browse" to pick a file in sync mode.';
                return;
            }
            const file = e.dataTransfer.files[0];
            if (file) this._processFile(file, resultEl, onComplete);
        });
    },

    async _processFile(file, resultEl, onComplete) {
        // oneShot import — drop any previous sync handle so autosync stops re-reading.
        await setMeta(HANDLE_META_KEY, null);
        resultEl.className = 'hint';
        resultEl.textContent = 'Reading file...';
        try {
            const text = await file.text();
            const parsed = this._parseAndNormalise(text);
            if (parsed.error) {
                resultEl.className = 'hint hint--error';
                resultEl.textContent = `Error: ${parsed.error}`;
                return;
            }
            this._logSkipped(parsed.skipped);
            const message = this._formatImportMessage(parsed.valid.length, parsed.skipped.length, 'Imported');
            resultEl.className = 'hint hint--success';
            resultEl.textContent = message;
            onComplete({ rides: parsed.valid, message });
        } catch (e) {
            reportError(e, { adapter: 'file-import', op: 'process-file' });
            resultEl.className = 'hint hint--error';
            resultEl.textContent = `Error: ${e.message}`;
        }
    },

    async _processHandle(handle, resultEl, container, onComplete) {
        resultEl.className = 'hint';
        resultEl.textContent = 'Reading file...';
        try {
            await setMeta(HANDLE_META_KEY, handle);
            const result = await this.sync(msg => { resultEl.textContent = msg; });
            if (!result.message) {
                await setMeta(HANDLE_META_KEY, null);
                resultEl.className = 'hint hint--error';
                resultEl.textContent = 'Could not read file. Permission denied.';
                return;
            }
            resultEl.className = 'hint hint--success';
            resultEl.textContent = result.message;
            onComplete(result);
            await this.renderConfig(container, onComplete, this._onDisconnect);
        } catch (e) {
            reportError(e, { adapter: 'file-import', op: 'process-handle' });
            await setMeta(HANDLE_META_KEY, null);
            resultEl.className = 'hint hint--error';
            resultEl.textContent = `Error: ${e.message}`;
        }
    },

    async sync(progressCb) {
        const handle = await getMeta(HANDLE_META_KEY);
        if (!handle) return { rides: [], message: '' };

        const perm = handle.queryPermission
            ? await handle.queryPermission({ mode: 'read' })
            : 'granted';
        if (perm !== 'granted') {
            try {
                const granted = await handle.requestPermission({ mode: 'read' });
                if (granted !== 'granted') {
                    console.info('file-import sync: read permission not granted');
                    return { rides: [], message: '' };
                }
            } catch {
                // requestPermission throws SecurityError when called without a user gesture
                console.info('file-import sync: requestPermission requires a user gesture');
                return { rides: [], message: '' };
            }
        }

        progressCb?.('Reading file...');
        const file = await handle.getFile();
        const text = await file.text();
        const parsed = this._parseAndNormalise(text);
        if (parsed.error) throw new Error(parsed.error);

        this._logSkipped(parsed.skipped);
        const message = this._formatImportMessage(parsed.valid.length, parsed.skipped.length, 'Synced');
        return { rides: parsed.valid, message };
    },

    _parseAndNormalise(text) {
        let json;
        try {
            json = JSON.parse(text);
        } catch (err) {
            console.warn('[file-import] Invalid JSON file', err);
            reportError(err, { adapter: 'file-import', op: 'parse-json' });
            return { error: 'Invalid JSON file.' };
        }
        if (!json || !Array.isArray(json.tours)) {
            console.warn('[file-import] File must contain a "tours" array', json);
            reportError('File must contain a "tours" array.',
                { adapter: 'file-import', op: 'parse-schema' });
            return { error: 'File must contain a "tours" array.' };
        }
        const { valid, skipped } = normaliseRides(json.tours, { defaultSource: 'json-import' });
        return { valid, skipped };
    },

    _logSkipped(skipped) {
        if (skipped.length === 0) return;
        console.warn(`[file-import] Skipped ${skipped.length} entr${skipped.length === 1 ? 'y' : 'ies'}`, skipped);
    },

    _formatImportMessage(validCount, skipped, verb) {
        const rideWord = validCount === 1 ? 'ride' : 'rides';
        return skipped > 0
            ? `${verb} ${validCount} ${rideWord}. ${skipped} entr${skipped === 1 ? 'y' : 'ies'} skipped.`
            : `${verb} ${validCount} ${rideWord}.`;
    },

    // Disconnect: forget the synced file handle (settings clears activeAdapter +
    // leaves ride data). The view then falls back to the file picker.
    async revokeAccess() {
        await setMeta(HANDLE_META_KEY, null);
    },

    cleanup() {}
};

export default fileImportAdapter;
